class TmPhoneEl {
    static $instances = [];

    static instance(identifier) {
        return TmPhoneEl.$instances[identifier] ?? null;
    }

    // options (per instance): keepPlaceholder — leave the library's example-number placeholder on the input (it is
    // stripped on focus otherwise, for the floating-label fields); maskPlaceholder — show that example with its digits
    // as X ("(XXX) XXX-XXXX") instead of a sample number; initialCountry — ISO2 to start on (default 'us');
    // linkStyle — the Stripe Link-style field the Cool checkout uses (Peter, 2026-09-12/13/14): left empty it is a
    // plain field with its label resting as the placeholder; focused or holding a number, the label floats and a
    // flag + chevron block sits at the left with the number in national format after it; the country picker is the browser's own <select> laid over the
    // flag (the visitor's country first, then the "list first" countries, then the rest A–Z). CSS: cool-typography.scss.
    static add(querySelector, identifier, forceUpdate = false, options = {}) {
        if (querySelector && identifier.length > 0 && (!TmPhoneEl.instance(identifier) || forceUpdate)) {
            TmPhoneEl.$instances[identifier] = new TmPhoneEl(querySelector, identifier, options);
        }
        return TmPhoneEl.instance(identifier);
    }

    constructor(querySelector, identifier, options = {}) {
        this.querySelector = querySelector;
        this.identifier = identifier;
        this.options = options;
        try {
            this.iti = window.intlTelInput(querySelector, {
                strictMode: true,
                initialCountry: options.initialCountry || 'us', // the checkout passes the visitor's country (Cloudflare header)
                containerClass: options.linkStyle ? 'c-link-phone-iti' : 'new-phone-input',
                nationalMode: true,
                separateDialCode: !options.linkStyle, // link style shows its own "+1" span (below)
                formatOnDisplay: true,
                formatAsYouType: true,
                useFullscreenPopup: false,
                // the "list first" countries; the visitor's country (options.initialCountry, from Cloudflare on the checkout)
                // takes the #1 spot — moved up if it is in the list, added if not (Peter, 2026-09-12)
                countryOrder: [(options.initialCountry || 'us').toLowerCase()].concat(
                    ['us', 'au', 'ca', 'fr', 'de', 'ie', 'it', 'nl', 'nz', 'gb'].filter(function (c) { return c !== (options.initialCountry || 'us').toLowerCase(); })
                ),
                i18n: { us: 'USA', gb: 'UK' }, // dropdown names (v25: i18n; there is no countryNameOverrides) — Peter, 2026-09-12
                customPlaceholder: options.maskPlaceholder ? function (example) { return example.replace(/\d/g, 'X'); } : undefined,
            });

            this.querySelector.addEventListener("input", function () {
                this.updateClassForNumber(document.activeElement === this.querySelector);
            }.bind(this));

            this.querySelector.addEventListener("focus", function () {
                this.updateClassForNumber(true);
            }.bind(this));

            this.querySelector.addEventListener("blur", function (e) {
                // link style: focus moving to the country <select> (a click on the flag) counts as staying in the field
                this.updateClassForNumber(!!(this.selectEl && e.relatedTarget === this.selectEl));
            }.bind(this));

            this.querySelector.addEventListener("countrychange", function () {
                this.syncLinkStyle();
                this.publishPrefixWidth();
            }.bind(this));

            this.querySelector.addEventListener("countrychange", () => {
                jQuery(document).trigger('tmPhoneEl_country_change___' + this.identifier, {countryData: this.iti.getSelectedCountryData()});
            });

            if (options.linkStyle) {
                this.setUpLinkStyle();
            }
            this.updateClassForNumber();
        } catch (e) {}
    }

    // linkStyle: the browser <select> over the flag and the wrapper class the CSS keys off
    setUpLinkStyle() {
        let input = this.querySelector;
        let wrap = input.closest('.iti');
        let field = input.closest('.custom-input');
        if (!wrap) { return; }
        if (field) { field.classList.add('c-link-phone'); }

        // no dial code in the field (Peter, 2026-09-14): the flag + chevron block is the only prepend; the country's
        // code still shows in the <select> labels and the number is stored E.164 (getNumber()) as before
        this.dialEl = null;

        let visitor = (this.options.initialCountry || 'us').toLowerCase();
        let listFirst = [visitor].concat(['us', 'au', 'ca', 'fr', 'de', 'ie', 'it', 'nl', 'nz', 'gb'].filter(function (c) { return c !== visitor; }));
        let all = window.intlTelInput.getCountryData();
        let select = document.createElement('select');
        select.className = 'c-link-phone__country';
        select.setAttribute('aria-label', 'Country');
        let addOption = function (c) {
            let o = document.createElement('option');
            o.value = c.iso2;
            o.textContent = (c.iso2 === 'us' ? 'USA' : (c.iso2 === 'gb' ? 'UK' : c.name)) + ' (+' + c.dialCode + ')';
            select.appendChild(o);
        };
        listFirst.forEach(function (iso2) { let c = all.find(function (x) { return x.iso2 === iso2; }); if (c) { addOption(c); } });
        let sep = document.createElement('option'); sep.disabled = true; sep.textContent = '─────'; select.appendChild(sep);
        all.filter(function (c) { return listFirst.indexOf(c.iso2) === -1; })
            .sort(function (a, b) { return a.name.localeCompare(b.name); })
            .forEach(addOption);
        let container = wrap.querySelector('.iti__country-container');
        if (container) { container.appendChild(select); } // over the flag button, inside its stacking context
        this.selectEl = select;

        select.addEventListener('focus', function () { this.updateClassForNumber(true); }.bind(this));
        select.addEventListener('blur', function (e) { this.updateClassForNumber(e.relatedTarget === input); }.bind(this));
        select.addEventListener('change', function () { this.iti.setCountry(select.value); input.focus(); }.bind(this));
        this.syncLinkStyle();
    }

    syncLinkStyle() {
        if (!this.options.linkStyle) { return; }
        let d = this.iti.getSelectedCountryData();
        if (this.dialEl) { this.dialEl.textContent = d && d.dialCode ? '+' + d.dialCode : ''; }
        if (this.selectEl && d && d.iso2) { this.selectEl.value = d.iso2; }
    }

    updateClassForNumber(isFocused = false) {
        // active = a number in the field, or the caret in it: the label floats and (link style) the flag block shows.
        // Left with nothing in it, the field is the original one again — "Phone number" resting as the placeholder,
        // no flag block (Peter, 2026-09-14; the block used to stay once the field had ever been focused).
        let active = this.querySelector.value.trim() || isFocused;
        if (active) {
            this.querySelector.classList.remove("empty");
            this.querySelector.closest(".iti").classList.add("number-entered");
            if (!this.options.keepPlaceholder) {
                this.querySelector.removeAttribute('placeholder');
            }
        }
        else {
            this.querySelector.classList.add("empty");
            this.querySelector.closest(".iti").classList.remove("number-entered");
        }
        this.syncLinkStyle();
        this.publishPrefixWidth();
    }

    // two widths on the .iti wrapper for the checkout's CSS: --tm-phone-flag-width = the flag + chevron block (the
    // grey prepend; the labels start after it) and --tm-phone-prefix-width = the whole country container (the
    // number starts after it). Both change with the country.
    publishPrefixWidth() {
        let iti = this.querySelector.closest('.iti');
        let container = iti ? iti.querySelector('.iti__country-container') : null;
        let flagBlock = iti ? iti.querySelector('.iti__selected-country-primary') : null;
        if (container) {
            iti.style.setProperty('--tm-phone-prefix-width', container.offsetWidth + 'px');
        }
        if (flagBlock) {
            iti.style.setProperty('--tm-phone-flag-width', flagBlock.offsetWidth + 'px');
        }
    }

    getNumber() {
        let number = '';
        try {
            number = this.iti.getNumber();
        } catch (e) {}
        return number;
    }

    setNumber(value) {
        try {
            this.iti.setNumber(value);
        } catch (e) {}
    }

    getFormattedNumber() {
        let number = '';
        try {
            let selectedCountryData = this.iti.getSelectedCountryData();
            number = intlTelInput.utils.formatNumber(
                this.iti.getNumber(),
                selectedCountryData.iso,
                'NATIONAL'
            );
            number = '+' + selectedCountryData.dialCode + ' ' + number;
        } catch (e) {}
        return number;
    }

    isValidNumber() {
        let isValid = false;
        try {
            isValid = this.iti.isValidNumber();
        } catch (e) {}
        return isValid;
    }
}
